import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, Check, X, Trash2, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

/**
 * AdminThankYouWallTile — moderates `donor_thanks` submissions
 * coming from /support's "I just donated — add me" form.
 */
const AdminThankYouWallTile = () => {
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = async (status = statusFilter) => {
    setLoading(true);
    const { data } = await apiFetch(`/api/admin/donor-thanks?status=${status}`);
    setEntries(data?.entries || []);
    if (data?.counts) setCounts(data.counts);
    setLoading(false);
  };

  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const onApprove = async (id) => {
    const { error } = await apiFetch(`/api/admin/donor-thanks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'approved' }),
    });
    if (error) { toast.error('Approve failed'); return; }
    toast.success('Approved — now visible on /support');
    load();
  };

  const onReject = async (id) => {
    const { error } = await apiFetch(`/api/admin/donor-thanks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'rejected' }),
    });
    if (error) { toast.error('Reject failed'); return; }
    toast.success('Rejected');
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Permanently delete this entry?')) return;
    await apiFetch(`/api/admin/donor-thanks/${id}`, { method: 'DELETE' });
    toast.success('Deleted');
    load();
  };

  const TABS = [
    { id: 'pending', label: 'Pending', n: counts.pending },
    { id: 'approved', label: 'Approved', n: counts.approved },
    { id: 'rejected', label: 'Rejected', n: counts.rejected },
    { id: 'all', label: 'All' },
  ];

  return (
    <Card data-testid="admin-thank-you-wall-tile" className="mt-6 border-pink-300/40">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-pink-500 fill-pink-500" />
            Thank-you wall moderation
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => load()}
            data-testid="thank-you-wall-refresh"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </CardTitle>
        <CardDescription className="text-xs">
          Approve donor opt-in entries before they appear on the public /support page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Status tabs */}
        <div className="flex flex-wrap gap-1" data-testid="thank-you-wall-tabs">
          {TABS.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={statusFilter === t.id ? 'default' : 'outline'}
              onClick={() => setStatusFilter(t.id)}
              data-testid={`thank-you-tab-${t.id}`}
              className={statusFilter === t.id ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}
            >
              {t.label}
              {typeof t.n === 'number' && (
                <Badge variant="secondary" className="ml-1.5 text-[10px]">{t.n}</Badge>
              )}
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="thank-you-wall-empty">
            No {statusFilter === 'all' ? '' : statusFilter} entries.
          </p>
        ) : (
          <div className="space-y-2" data-testid="thank-you-wall-list">
            {entries.map((e) => (
              <div
                key={e.thank_id}
                className="rounded border p-3 flex items-start gap-2 text-xs"
                data-testid={`thank-you-row-${e.thank_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-baseline gap-2">
                    {e.display_name}
                    {e.amount_usd != null && (
                      <span className="text-[11px] font-mono text-muted-foreground">
                        ${Number(e.amount_usd).toFixed(0)}
                      </span>
                    )}
                    <Badge
                      variant={e.status === 'approved' ? 'default' : e.status === 'rejected' ? 'destructive' : 'secondary'}
                      className="text-[10px] ml-auto"
                    >
                      {e.status}
                    </Badge>
                  </div>
                  {e.message && (
                    <p className="text-muted-foreground italic mt-1">“{e.message}”</p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Submitted {new Date(e.submitted_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {e.status !== 'approved' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onApprove(e.thank_id)}
                      data-testid={`thank-you-approve-${e.thank_id}`}
                      title="Approve"
                    >
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    </Button>
                  )}
                  {e.status !== 'rejected' && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onReject(e.thank_id)}
                      data-testid={`thank-you-reject-${e.thank_id}`}
                      title="Reject"
                    >
                      <X className="h-3.5 w-3.5 text-amber-600" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => onDelete(e.thank_id)}
                    data-testid={`thank-you-delete-${e.thank_id}`}
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminThankYouWallTile;
